/**
 * SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/platform-admin/IPWhitelistManagement.tsx
 * PURPOSE: IP Whitelist Management for Platform Admin - Sprint 28 ADR-039 Track C
 * SDLC FRAMEWORK VERSION: 6.3.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance | Rule 9: Documentation Standards
 *
 * DATE: 2026-03-28
 * COMPLIANCE: ADR-039 Enterprise Platform Admin RBAC Architecture v3.1.0
 *
 * @business-context
 * - Domain: Platform Administration, Security, Access Control
 * - Impact: Network security, IP-based access restriction
 * - Stakeholders: SECURITY_ADMIN, PLATFORM_OPERATOR
 *
 * @technical-architecture
 * - Layer: Presentation/UI
 * - Pattern: React functional component with CSS Modules + NQH design tokens
 * - Dependencies: React, platformAdminApi, CSS Modules
 *
 * @cross-references
 * - API: backend/app/api/v1/platform_admin.py (GET/POST/DELETE /ip-whitelist)
 * - Design: docs/02-design/04-ui-ux-design/NQH-BRAND-DESIGN-SYSTEM.md
 * - Sprint: S75-010 (CSS modules migration + Vietnamese labels)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { usePlatformAdminAuth } from '../../contexts/PlatformAdminContext';
import {
  platformAdminApi,
  IPWhitelistEntry,
  IPWhitelistCreateRequest,
  IPWhitelistListResponse,
  IPWhitelistCheckResponse,
} from '../../services/api/platform-admin/platformAdmin';
import styles from './IPWhitelistManagement.module.css';

const IPWhitelistManagement: React.FC = () => {
  const { platformAdmin } = usePlatformAdminAuth();
  const accessToken = platformAdmin.accessToken || '';

  const canEdit = platformAdmin.admin?.role === 'platform_super_admin' ||
                  platformAdmin.admin?.role === 'platform_security';

  const [entries, setEntries] = useState<IPWhitelistEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [newEntry, setNewEntry] = useState<IPWhitelistCreateRequest>({
    ip_address: '',
    cidr_prefix: undefined,
    description: '',
    expires_at: undefined,
  });
  const [adding, setAdding] = useState(false);

  const [checkIP, setCheckIP] = useState('');
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<IPWhitelistCheckResponse | null>(null);

  const [deleteEntryId, setDeleteEntryId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response: IPWhitelistListResponse = await platformAdminApi.listIPWhitelist(
        accessToken,
        includeInactive
      );
      setEntries(response.items);
      setTotal(response.total);
    } catch (err: any) {
      setError(err.message || 'Không thể tải danh sách IP whitelist');
    } finally {
      setLoading(false);
    }
  }, [accessToken, includeInactive]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleAddEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdding(true);
    setError(null);
    setSuccess(null);

    try {
      await platformAdminApi.addIPWhitelistEntry(accessToken, newEntry);
      setSuccess(`Địa chỉ IP ${newEntry.ip_address} đã được thêm vào whitelist`);
      setNewEntry({
        ip_address: '',
        cidr_prefix: undefined,
        description: '',
        expires_at: undefined,
      });
      fetchEntries();
    } catch (err: any) {
      setError(err.message || 'Không thể thêm IP vào whitelist');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveEntry = async () => {
    if (!deleteEntryId) return;

    setDeleting(true);
    setError(null);
    setSuccess(null);

    try {
      await platformAdminApi.removeIPWhitelistEntry(accessToken, deleteEntryId);
      setSuccess('Đã xóa mục IP whitelist');
      setDeleteEntryId(null);
      fetchEntries();
    } catch (err: any) {
      setError(err.message || 'Không thể xóa mục IP whitelist');
    } finally {
      setDeleting(false);
    }
  };

  const handleCheckIP = async () => {
    if (!checkIP.trim()) return;

    setChecking(true);
    setCheckResult(null);
    setError(null);

    try {
      const result = await platformAdminApi.checkIPWhitelisted(accessToken, checkIP.trim());
      setCheckResult(result);
    } catch (err: any) {
      setError(err.message || 'Không thể kiểm tra địa chỉ IP');
    } finally {
      setChecking(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isExpired = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const getEntryStatus = (entry: IPWhitelistEntry) => {
    if (!entry.is_active) return { label: 'Không hoạt động', badgeClass: styles.inactiveBadge };
    if (isExpired(entry.expires_at)) return { label: 'Hết hạn', badgeClass: styles.expiredBadge };
    return { label: 'Hoạt động', badgeClass: styles.activeBadge };
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Quản lý IP Whitelist</h1>
          <p className={styles.subtitle}>
            Quản lý danh sách địa chỉ IP được phép truy cập quản trị nền tảng
          </p>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className={styles.error}>
          {error}
          <button className={styles.errorDismiss} onClick={() => setError(null)}>
            Bỏ qua
          </button>
        </div>
      )}
      {success && (
        <div className={styles.success}>
          {success}
          <button className={styles.successDismiss} onClick={() => setSuccess(null)}>
            Bỏ qua
          </button>
        </div>
      )}

      {/* Add New Entry Form */}
      {canEdit && (
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>Thêm mục IP Whitelist</h3>
          <form onSubmit={handleAddEntry}>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Địa chỉ IP *</label>
                <input
                  type="text"
                  className={styles.formInput}
                  placeholder="VD: 192.168.1.100 hoặc 2001:db8::1"
                  value={newEntry.ip_address}
                  onChange={(e) => setNewEntry({ ...newEntry, ip_address: e.target.value })}
                  required
                />
                <span className={styles.formHint}>Địa chỉ IPv4 hoặc IPv6</span>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Tiền tố CIDR (tùy chọn)</label>
                <input
                  type="number"
                  className={styles.formInput}
                  placeholder="VD: 24 cho /24"
                  min="0"
                  max="128"
                  value={newEntry.cidr_prefix || ''}
                  onChange={(e) => setNewEntry({
                    ...newEntry,
                    cidr_prefix: e.target.value ? parseInt(e.target.value) : undefined
                  })}
                />
                <span className={styles.formHint}>Để trống nếu khớp chính xác IP</span>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Mô tả</label>
                <input
                  type="text"
                  className={styles.formInput}
                  placeholder="VD: Mạng văn phòng"
                  value={newEntry.description || ''}
                  onChange={(e) => setNewEntry({ ...newEntry, description: e.target.value })}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Ngày hết hạn (tùy chọn)</label>
                <input
                  type="datetime-local"
                  className={styles.formInput}
                  value={newEntry.expires_at || ''}
                  onChange={(e) => setNewEntry({ ...newEntry, expires_at: e.target.value })}
                />
                <span className={styles.formHint}>Để trống nếu truy cập vĩnh viễn</span>
              </div>
            </div>

            <div className={styles.buttonRow}>
              <button
                type="submit"
                className={`${styles.button} ${styles.primaryButton}`}
                disabled={adding || !newEntry.ip_address}
              >
                {adding ? 'Đang thêm...' : 'Thêm IP vào Whitelist'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Check IP Tool */}
      <div className={styles.card}>
        <h3 className={styles.cardTitle}>Kiểm tra địa chỉ IP</h3>
        <div className={styles.checkRow}>
          <div className={styles.checkInputGroup}>
            <label className={styles.formLabel}>Địa chỉ IP cần kiểm tra</label>
            <input
              type="text"
              className={styles.formInput}
              placeholder="VD: 192.168.1.100"
              value={checkIP}
              onChange={(e) => setCheckIP(e.target.value)}
            />
          </div>
          <button
            className={`${styles.button} ${styles.successButton}`}
            onClick={handleCheckIP}
            disabled={checking || !checkIP.trim()}
          >
            {checking ? 'Đang kiểm tra...' : 'Kiểm tra'}
          </button>
        </div>

        {checkResult && (
          <div
            className={`${styles.checkResult} ${checkResult.is_whitelisted ? styles.checkAllowed : styles.checkDenied}`}
          >
            <strong>{checkResult.ip_address}</strong>{' '}
            {checkResult.is_whitelisted ? (
              <>
                <strong>nằm trong whitelist</strong>
                {checkResult.matched_entry_id && (
                  <span className={styles.checkMatchInfo}>
                    (mục khớp: {checkResult.matched_entry_id.substring(0, 8)}...)
                  </span>
                )}
              </>
            ) : (
              <strong>không nằm trong whitelist</strong>
            )}
          </div>
        )}
      </div>

      {/* Whitelist Table */}
      <div className={styles.tableContainer}>
        <div className={styles.tableHeader}>
          <span className={styles.tableHeaderTitle}>
            Danh sách IP Whitelist ({total})
          </span>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Hiển thị mục không hoạt động
          </label>
        </div>

        {loading ? (
          <div className={styles.loading}>Đang tải danh sách IP whitelist...</div>
        ) : entries.length === 0 ? (
          <div className={styles.emptyState}>
            Không tìm thấy mục IP whitelist nào.
            {canEdit && ' Thêm mục đầu tiên ở trên.'}
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Địa chỉ IP</th>
                <th className={styles.th}>CIDR</th>
                <th className={styles.th}>Mô tả</th>
                <th className={styles.th}>Trạng thái</th>
                <th className={styles.th}>Ngày tạo</th>
                <th className={styles.th}>Hết hạn</th>
                {canEdit && <th className={styles.th}>Hành động</th>}
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => {
                const status = getEntryStatus(entry);
                return (
                  <tr key={entry.id}>
                    <td className={styles.td}>
                      <code className={styles.ipCode}>
                        {entry.ip_address}
                      </code>
                    </td>
                    <td className={styles.td}>
                      {entry.cidr_prefix !== null ? `/${entry.cidr_prefix}` : '-'}
                    </td>
                    <td className={styles.td}>{entry.description || '-'}</td>
                    <td className={styles.td}>
                      <span className={`${styles.badge} ${status.badgeClass}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className={styles.td}>{formatDate(entry.created_at)}</td>
                    <td className={styles.td}>{formatDate(entry.expires_at)}</td>
                    {canEdit && (
                      <td className={styles.td}>
                        <button
                          className={styles.deleteButton}
                          onClick={() => setDeleteEntryId(entry.id)}
                          disabled={!entry.is_active}
                        >
                          Xóa
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteEntryId && (
        <div className={styles.modal} onClick={() => setDeleteEntryId(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Xác nhận xóa</h3>
            <p className={styles.modalText}>
              Bạn có chắc chắn muốn xóa mục IP whitelist này? Thao tác này sẽ vô hiệu hóa mục.
            </p>
            <div className={styles.buttonRow}>
              <button
                className={`${styles.button} ${styles.secondaryButton}`}
                onClick={() => setDeleteEntryId(null)}
              >
                Hủy
              </button>
              <button
                className={`${styles.button} ${styles.dangerButton}`}
                onClick={handleRemoveEntry}
                disabled={deleting}
              >
                {deleting ? 'Đang xóa...' : 'Xóa'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IPWhitelistManagement;
